/**
 * <LiveActivitiesSheet> — « Mes activités », la todo du live (template).
 *
 * L'icône de liste de l'en-tête du live (le « burger » d'aujourd'hui) l'ouvre : les activités que
 * le tuteur a mises pour cette séance, chacune sur sa ligne (`<ActivityRow>` : la matière, le
 * titre, le chapitre, son statut — « En cours » pour celle qui est ouverte). Un toucher ouvre
 * l'activité et ferme la feuille ; c'est l'écran qui le fait.
 *
 * Au-dessus d'elles, les activités faites (`doneActivities`), dans l'ordre de l'onglet Activités
 * et de l'ancienne liste du live (`TodoListModalItems`) : le bloc « Faites », plié, son compte, puis
 * ses lignes, et dessous « À faire » (Wajih, 20/10). Un toucher ouvre le rendu de l'activité
 * (`onSelectDone`). Le bloc n'est là que s'il y a une activité faite ; alors les activités à faire
 * prennent aussi leur intitulé, « À faire ».
 *
 * Et les gestes de l'ancienne liste du live (`TodoListModal`), quand l'écran les passe : relire la
 * liste (`onRefresh`), sa flèche à droite de « À faire », sur la même ligne, comme « Afficher » à
 * côté de « Faites » (Nada, 20/10 : seule sur sa ligne, elle laissait une ligne vide) — l'intitulé
 * « À faire » paraît donc aussi pour elle ; et « Voir plus » sous les activités faites (les réalisations). « Générer une
 * activité » n'a pas sa place pendant le live (Wajih, 20/10) : la feuille ne l'offre pas.
 *
 * Une feuille basse sur un téléphone, une carte au milieu sur un grand écran (`<Modal>`). Plus
 * d'activité : la phrase de l'écran du live, sans champion.
 *
 * Contrôlée : la liste arrive en props, le toucher remonte.
 */
import React from "react"

import { type UIActivity } from "../../../ports/primary/ui/types"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { ActivityRow } from "../../molecules/ActivityRow"
import { CardList } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { Modal } from "../../molecules/Modal"
import { DoneActivitiesAccordion } from "../ActivitiesSection/DoneActivitiesAccordion"
import { RefreshButton } from "../../molecules/RefreshButton"

export type LiveActivitiesSheetProps = {
    visible: boolean
    onClose: () => void
    activities: UIActivity[]
    /** Une ligne touchée : l'écran ouvre l'activité. */
    onSelect: (activity: UIActivity) => void
    /** Les activités faites, celles de l'onglet Activités. Défaut : aucune, pas de bloc « Faites ». */
    doneActivities?: UIActivity[]
    /** Une activité faite touchée : l'écran ouvre son rendu. */
    onSelectDone?: (activity: UIActivity) => void
    /** Relire la liste. Absent (défaut) : pas de flèche. */
    onRefresh?: () => void
    /** La relecture court. Défaut `false`. */
    refreshing?: boolean
    /** « Voir plus » sous les activités faites : les réalisations. Absent : pas de lien. */
    onSeeAllDone?: () => void
    testID?: string
}

const TEXTS = {
    title: "Mes activités",
    emptyTitle: "Tu n'as plus d'activités",
    emptyMessage: "Ton masteur va t'en ajouter sous peu.",
    todo: "À faire",
}

const NO_ACTIVITIES: UIActivity[] = []

export function LiveActivitiesSheet(props: LiveActivitiesSheetProps) {
    const {
        visible,
        onClose,
        activities,
        onSelect,
        doneActivities = NO_ACTIVITIES,
        onSelectDone,
        onRefresh,
        refreshing = false,
        onSeeAllDone,
        testID,
    } = props
    const withDone = doneActivities.length > 0
    // L'intitulé « À faire » : sous les activités faites, ou pour porter la flèche de relecture.
    const withHeading = withDone || onRefresh != null

    const todoList =
        activities.length === 0 ? (
            <EmptyState title={TEXTS.emptyTitle} message={TEXTS.emptyMessage} iconName="tasks" />
        ) : (
            <CardList>
                {activities.map((activity) => (
                    <ActivityRow key={activity.id} activity={activity} onPress={onSelect} />
                ))}
            </CardList>
        )
    const todo = !withHeading ? (
        todoList
    ) : (
        <Stack gap="3">
            <Row justify="between" align="center">
                <Row gap="2" align="center">
                    <SectionLabel>{TEXTS.todo}</SectionLabel>
                    <Badge text={String(activities.length)} variant="primary" tinted />
                </Row>
                {onRefresh != null && <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />}
            </Row>
            {todoList}
        </Stack>
    )

    return (
        <Modal visible={visible} onClose={onClose} title={TEXTS.title} sizeToContent testID={testID}>
            {!withDone ? (
                todo
            ) : (
                <Stack gap="6">
                    {/* Le bloc « Faites » de l'onglet Activités, plié comme lui, et au-dessus comme lui. */}
                    <DoneActivitiesAccordion
                        activities={doneActivities}
                        onPress={onSelectDone}
                        onSeeAll={onSeeAllDone}
                    />
                    {todo}
                </Stack>
            )}
        </Modal>
    )
}
